import React, { Fragment, useState, useEffect, useCallback, useMemo } from "react";
import {
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Button,
    Input,
    Form,
} from "reactstrap";
import DataTable from "react-data-table-component";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { Eye } from "react-feather";
import { ToastContainer, toast } from "react-toastify";
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import AxiosInstance from "../../services/AxiosInstance";
import Spinner from "../../Components/Common/Spinner";
import NoDataFound from "../../Components/Common/NoDataFound";
import { customStyles } from "../../Constant";
import "react-toastify/dist/ReactToastify.css";

const PAGE_SIZES = [10, 25, 50, 100];

const JOB_STATUS_OPTIONS = [
    { value: "ACTIVE", label: "Active" },
    { value: "DRAFT", label: "Draft" },
    { value: "UNDER_REVIEW", label: "Under Review" },
    { value: "CLOSED", label: "Closed" },
    { value: "WORK_IN_PROGRESS", label: "Work In Progress" },
    { value: "ON_HOLD", label: "On Hold" },
];

const ManageJobs = () => {
    const [jobs, setJobs] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchApplied, setSearchApplied] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [sortColumn, setSortColumn] = useState("created_at");
    const [sortDirection, setSortDirection] = useState("desc");
    const [updatingStatusId, setUpdatingStatusId] = useState(null);

    const animatedComponents = makeAnimated();
    const selectColors = {
        primary: "#007bff",
        primary25: "#cce5ff",
        approved: "green",
        rejected: "red",
        hold: "orange",
        lightGray: "#f8f9fa",
        darkGray: "#343a40",
    };
    const getStatusColor = (value) => {
        switch (value) {
            case "ACTIVE": return selectColors.approved;
            case "ON_HOLD": return selectColors.hold;
            case "CLOSED": return selectColors.rejected;
            case "UNDER_REVIEW": return selectColors.rejected;
            case "WORK_IN_PROGRESS": return selectColors.primary;
            case "DRAFT": return selectColors.darkGray;
            default: return selectColors.darkGray;
        }
    };
    const customStylesSelect = {
        control: (provided, state) => ({
            ...provided,
            minWidth: "160px",
            borderColor: state.isFocused ? selectColors.primary : provided.borderColor,
            boxShadow: state.isFocused ? `0 0 0 1px ${selectColors.primary}` : provided.boxShadow,
        }),
        singleValue: (provided, state) => ({
            ...provided,
            color: getStatusColor(state.data?.value),
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? getStatusColor(state.data.value) : state.isFocused ? selectColors.primary25 : selectColors.lightGray,
            color: state.isSelected || state.isFocused ? "#fff" : selectColors.darkGray,
        }),
    };

    const fetchJobs = useCallback(async (page, perPageSize, search, sortBy, sortDir) => {
        setLoading(true);
        setError(null);
        try {
            const res = await AxiosInstance().post("/admin/fetch_all_jobs", {
                page,
                per_page: perPageSize,
                search: (search || "").trim() || undefined,
                sort_by: sortBy || "created_at",
                sort_dir: sortDir || "desc",
            });
            setJobs(Array.isArray(res.data?.data) ? res.data.data : []);
            setTotalRows(typeof res.data?.total === "number" ? res.data.total : 0);
        } catch (e) {
            console.error(e);
            toast.error(e.response?.data?.message || "Failed to load jobs");
            setJobs([]);
            setTotalRows(0);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchJobs(currentPage, perPage, searchApplied, sortColumn, sortDirection);
    }, [currentPage, perPage, searchApplied, sortColumn, sortDirection, fetchJobs]);

    const loadData = useCallback(() => {
        fetchJobs(currentPage, perPage, searchApplied, sortColumn, sortDirection);
    }, [fetchJobs, currentPage, perPage, searchApplied, sortColumn, sortDirection]);

    const formatDate = (dateStr) => {
        if (!dateStr) return "—";
        try {
            const d = new Date(dateStr);
            return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString();
        } catch {
            return dateStr;
        }
    };

    const handleStatusChange = async (jobId, newStatus) => {
        setUpdatingStatusId(jobId);
        try {
            const res = await AxiosInstance().post("/admin/update_job_status", {
                job_id: jobId,
                status: newStatus,
            });
            if (res.data.status) {
                toast.success(res.data.message || "Status updated");
                fetchJobs(currentPage, perPage, searchApplied, sortColumn, sortDirection);
            } else {
                toast.error(res.data.message || "Update failed");
            }
        } catch (e) {
            toast.error(e.response?.data?.message || "Update failed");
        } finally {
            setUpdatingStatusId(null);
        }
    };

    const handleSearchSubmit = (e) => {
        e?.preventDefault();
        setSearchApplied(searchQuery);
        setCurrentPage(1);
    };

    const handlePageChange = (page) => setCurrentPage(page);
    const handlePerRowsChange = (newPerPage, page) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const handleSort = (selectedColumn, direction) => {
        const field = selectedColumn?.id || "created_at";
        setSortColumn(field);
        setSortDirection(direction === "asc" ? "asc" : "desc");
        setCurrentPage(1);
    };

    const tableColumns = [
        {
            name: "#",
            cell: (row, index) => (currentPage - 1) * perPage + index + 1,
            width: "60px",
            sortable: false,
        },
        {
            id: "job_name",
            name: "Job Name",
            selector: (row) => row.job_name || "—",
            sortable: true,
            grow: 2,
            cell: (row) => <span className="fw-medium">{row.job_name || "—"}</span>,
        },
        {
            id: "company_name",
            name: "Company",
            selector: (row) => row.organization || "—",
            sortable: true,
            grow: 1.5,
        },
        {
            id: "job_location",
            name: "Location",
            selector: (row) => row.job_location || "—",
            sortable: true,
            width: "120px",
        },
        {
            name: "Status",
            width: "190px",
            center: true,
            cell: (row) => {
                const currentStatus = (row.job_status || "").toUpperCase();
                const valueOption = JOB_STATUS_OPTIONS.find((opt) => opt.value === currentStatus) || { value: currentStatus || "", label: currentStatus || "—" };
                return (
                    <Select
                        components={animatedComponents}
                        className="basic-single"
                        classNamePrefix="select"
                        value={valueOption}
                        onChange={(selectedOption) => selectedOption && handleStatusChange(row.job_id, selectedOption.value)}
                        options={JOB_STATUS_OPTIONS}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={{
                            ...customStylesSelect,
                            menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
                        }}
                        isDisabled={updatingStatusId === row.job_id}
                        isSearchable={false}
                        menuPlacement="auto"
                    />
                );
            },
        },
        {
            id: "created_at",
            name: "Created",
            selector: (row) => row.created_at || "—",
            sortable: true,
            width: "110px",
            cell: (row) => <span className="text-muted small">{formatDate(row.created_at)}</span>,
        },
        {
            name: "Actions",
            width: "110px",
            center: true,
            cell: (row) => (
                <div className="d-flex gap-1 justify-content-center">
                    <a
                        href={`${process.env.PUBLIC_URL || ""}/public/job_details/${row.job_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-light btn-sm p-1 px-2 d-inline-flex align-items-center justify-content-center"
                        title="View job (as jobseeker sees it)"
                    >
                        <Eye size={16} />
                    </a>
                    {/* Edit action – enable later when needed
                    <Button
                        color="primary"
                        size="sm"
                        outline
                        title="Edit job"
                        className="p-1 px-2"
                    >
                        <i className="fa fa-pencil-alt" />
                    </Button>
                    */}
                </div>
            ),
        },
    ];

    if (loading && jobs.length === 0) {
        return (
            <Fragment>
                <Breadcrumbs title="Manage Jobs" mainTitle="Admin" />
                <div className="d-flex justify-content-center align-items-center py-5">
                    <Spinner />
                </div>
            </Fragment>
        );
    }

    if (error && jobs.length === 0) {
        return (
            <Fragment>
                <Breadcrumbs title="Manage Jobs" mainTitle="Admin" />
                <Row>
                    <Col xs="12">
                        <Card>
                            <CardBody className="text-center py-5">
                                <p className="text-danger mb-0">{error}</p>
                                <Button color="primary" className="mt-3" onClick={loadData}>
                                    Retry
                                </Button>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                <ToastContainer />
            </Fragment>
        );
    }

    return (
        <Fragment>
            <Breadcrumbs title="Manage Jobs" mainTitle="Admin" />
            <Row>
                <Col xs="12" className="box-col-12 xl-100">
                    <Card>
                        <CardHeader className="py-3">
                            <div className="d-flex flex-wrap justify-content-between align-items-end mb-3">
                                <Form
                                    className="theme-form me-2 flex-grow-1"
                                    onSubmit={handleSearchSubmit}
                                >
                                    <div
                                        className="input-group flex-nowrap"
                                        style={{ height: 35, maxWidth: "350px" }}
                                    >
                                        <Input
                                            className="form-control"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            type="search"
                                            placeholder="Search by Job Name, Company or Location..."
                                        />
                                        <Button
                                            color="primary"
                                            type="submit"
                                            className="btn-primary input-group-text pe-3 ps-3"
                                        >
                                            Search
                                        </Button>
                                    </div>
                                </Form>
                                <div className="d-flex align-items-center ms-auto mt-3 mt-md-0">
                                    <Button color="primary" outline onClick={loadData} className="d-flex align-items-center">
                                        <i className="fa fa-sync-alt me-1" /> Refresh
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>

                        <CardBody className="px-0 py-3">
                            <div className="table-container">
                                <DataTable
                                    responsive
                                    customStyles={customStyles}
                                    data={jobs}
                                    columns={tableColumns}
                                    striped
                                    pagination
                                    paginationServer
                                    sortServer
                                    onSort={handleSort}
                                    defaultSortFieldId="created_at"
                                    defaultSortAsc={false}
                                    paginationTotalRows={totalRows}
                                    paginationPerPage={perPage}
                                    paginationRowsPerPageOptions={PAGE_SIZES}
                                    onChangePage={handlePageChange}
                                    onChangeRowsPerPage={handlePerRowsChange}
                                    progressPending={loading}
                                    progressComponent={<Spinner />}
                                    noDataComponent={<NoDataFound message="No jobs found" />}
                                />
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <ToastContainer />
        </Fragment>
    );
};

export default ManageJobs;
